import {
  SHAutocomplete,
  SHStack,
  SHToggleButtonOptions,
} from "@components/design-systems";
import { useTheme } from "@mui/material";
import React, { forwardRef, useMemo, useState } from "react";
import {
  EditMSFilterSwitchYesNoFieldPath,
  getMSFilterRenderedItemInitialProps,
  MSFilterRenderedItemProps,
  MSFilterSwitchYesNoInitialProps,
  MSTableFilterItemRef,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import { MSFilterResetIcon } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/reset";
import {
  ModelScreenerFilterDropdownLov,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { CheckSVG } from "@components/svgs";
import { Controller, useFormContext } from "react-hook-form";
import { useMount } from "react-use";
import {
  MSFilterDefaultValueConstants,
  MSFilterSwitchOptions,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import {
  getMSFilterSwitchYesNoToggleButtonSx,
  getMSFilterSwitchYesNoToggleButtonWidth,
  renderOptions,
  renderTags,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/switch-yes-no/utils";
import { isEqual } from "lodash";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { useWindowWidth } from "@react-hook/window-size";

export const MSFilterSwitchYesNo = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(({ itemPaths, itemSize, item, disabled }: MSFilterRenderedItemProps, ref) => {
  const { palette, ...otherThemeProps } = useTheme();
  const isSmallSize = itemSize === "small";
  const switchYesNoFieldPaths = itemPaths as EditMSFilterSwitchYesNoFieldPath;
  const {
    switchLabel,
    option: originalOption,
    defaultDropdownOption,
  } = getMSFilterRenderedItemInitialProps<MSFilterSwitchYesNoInitialProps>(
    item,
  ) || {};

  const { control, getValues, setValue } =
    useFormContext<ModelScreenerFilterDTO>();

  // States
  const [currentSwitchValue, setCurrentSwitchValue] = useState<
    string | null | undefined
  >();
  const [currentDropdownValue, setCurrentDropdownValue] = useState<
    ModelScreenerFilterDropdownLov[]
  >([]);

  const windowWidth = useWindowWidth();

  useMount(() => {
    let switchValue = getValues(switchYesNoFieldPaths.switchValue);
    if (!switchValue) {
      setValue(
        switchYesNoFieldPaths.switchValue,
        MSFilterDefaultValueConstants.SwitchValue,
      );
      switchValue = MSFilterDefaultValueConstants.SwitchValue;
    } else {
      switchValue = String(switchValue);
    }
    const dropdownValue = getValues(
      switchYesNoFieldPaths.lov,
    ) as ModelScreenerFilterDropdownLov[];

    setCurrentSwitchValue(switchValue);
    setCurrentDropdownValue(dropdownValue);
  });

  const handleOnSwitchValueChange = (
    value?: string | null,
  ): string | null | undefined => {
    if (value === currentSwitchValue || !value) return currentSwitchValue;
    setCurrentSwitchValue(value);
    return value;
  };

  const handleOnDropdownValueChange = (
    value?: ModelScreenerFilterDropdownLov[] | null,
  ): ModelScreenerFilterDropdownLov[] | undefined => {
    const optionValueFlatMap: (string | null | undefined)[] =
      value?.map((opt) => opt.value ?? opt.targetSubHeaderId) ?? [];
    const newValue = originalOption?.lov?.map((opt) => {
      return {
        ...opt,
        isSelected: optionValueFlatMap?.includes(
          opt.value ?? opt.targetSubHeaderId,
        ),
      };
    });
    if (!newValue) return;
    setCurrentDropdownValue(newValue);
    return newValue;
  };

  const handleOnReset = () => {
    setCurrentSwitchValue(MSFilterDefaultValueConstants.SwitchValue);
    setCurrentDropdownValue(defaultDropdownOption ?? []);
    setValue(
      switchYesNoFieldPaths.switchValue,
      MSFilterDefaultValueConstants.SwitchValue,
    );
    setValue(switchYesNoFieldPaths.lov, defaultDropdownOption);
  };

  const selectedDropdownOptions: ModelScreenerFilterDropdownLov[] =
    useMemo(() => {
      return currentDropdownValue?.filter((opt) => opt.isSelected);
    }, [currentDropdownValue]);

  const shouldDisplayResetButton = useMemo(() => {
    return (
      !isEqual(currentSwitchValue, MSFilterDefaultValueConstants.SwitchValue) ||
      !isEqual(currentDropdownValue, defaultDropdownOption)
    );
  }, [currentSwitchValue, currentDropdownValue, defaultDropdownOption]);

  return (
    <SHStack
      ref={ref}
      width={"100%"}
      bgcolor={palette.common.white}
      borderRadius={"12px"}
      alignItems={"start"}
      p={isSmallSize ? 1 : 1.5}
      gap={1.5}
      sx={{
        filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
      }}
    >
      <SHStack alignItems={"start"} width={"100%"} gap={1}>
        <SHStack
          direction={"row"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <MSFilterItemLabel size={itemSize} disabled={disabled}>
            {switchLabel}
          </MSFilterItemLabel>

          {shouldDisplayResetButton && (
            <MSFilterResetIcon onClick={handleOnReset} disabled={disabled} />
          )}
        </SHStack>

        <SHStack
          width={"100%"}
          sx={getMSFilterSwitchYesNoToggleButtonSx({
            theme: { palette, ...otherThemeProps },
            isSmallSize,
          })}
        >
          <Controller
            control={control}
            name={switchYesNoFieldPaths.switchValue}
            render={({ field: { ref, ...other } }) => (
              <SHToggleButtonOptions
                key={`switch-${currentSwitchValue}-${disabled}`}
                disabled={disabled}
                width={getMSFilterSwitchYesNoToggleButtonWidth(windowWidth)}
                options={MSFilterSwitchOptions}
                value={currentSwitchValue}
                activeEndIcon={
                  <CheckSVG
                    width={isSmallSize ? 15 : 19}
                    stroke={palette.common.white}
                  />
                }
                onChange={(_, value) => {
                  const newValue = handleOnSwitchValueChange(value);
                  other.onChange(newValue);
                }}
                labelFontSize={isSmallSize ? 11 : 14}
                labelLineHeight={isSmallSize ? 0 : 1}
              />
            )}
          />
        </SHStack>
      </SHStack>

      <SHStack alignItems={"start"} width={"100%"} gap={1}>
        <MSFilterItemLabel size={itemSize} disabled={disabled}>
          {originalOption?.label}
        </MSFilterItemLabel>

        <SHStack width={"100%"}>
          <Controller
            control={control}
            name={switchYesNoFieldPaths.lov}
            render={({ field: { ref, ...other } }) => {
              return (
                <SHAutocomplete
                  disabled={disabled}
                  value={selectedDropdownOptions}
                  options={currentDropdownValue}
                  multiple
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name ?? ""}
                  onChange={(_, value) => {
                    const newValue = handleOnDropdownValueChange(value);
                    other.onChange(newValue);
                  }}
                  textFieldProps={{
                    placeholder: "Select options",
                  }}
                  renderTags={(value, getTagProps) =>
                    renderTags({
                      value,
                      getTagProps,
                      isSmallSize,
                    })
                  }
                  renderOption={(props, option, { selected, index }) =>
                    renderOptions({
                      props,
                      option,
                      selected,
                      index,
                    })
                  }
                  sx={{
                    ".MuiInput-root": {
                      height: "fit-content !important",
                    },
                  }}
                />
              );
            }}
          />
        </SHStack>
      </SHStack>
    </SHStack>
  );
});
